import { getReportLifecycleStatus } from "@utils/reportStatus";
import { Navigate, useLocation } from "react-router-dom";

import {
  ApiReportSectionsAiGeneratedStatusChoices,
  ApiReportTreatmentPlanAiStgPredictionStatusChoices,
  ApiReportTreatmentPlanAiTargetPredictionStatusChoices,
  ReportSectionsType,
} from "@api/graphql/types-and-hooks";
import { StatusType } from "@components/AssessmentReportsTable/AssessmentReportsTableV2";
import { InitialAssessmentPageRoute } from "@pages/AssessmentReportV2Details/types";
import { ReportNode, useReport } from "@providers/ReportProvider";

// TODO: BACKPORT: Will need to figure out a generic method for computing pages depending on report type
// NOTE: This is part of a much bigger pattern that needs to get figured out on the UI side
// which is how to handle dynamic routing based on report types
export const AssessmentReportV2DetailsRedirect = () => {
  const { state } = useLocation();
  const { report } = useReport();

  const pathToRedirectTo = getStatusAndRedirectRouteFromReport(report).redirectRoute;

  return <Navigate to={pathToRedirectTo} replace state={state} />;
};

export const getStatusAndRedirectRouteFromReport = (
  report?: ReportNode,
): { status: StatusType; redirectRoute: InitialAssessmentPageRoute } => {
  const getFilteredSections = (location: string): ReportSectionsType[] =>
    report?.reportSections?.edges
      ?.map((edge) => edge?.node)
      .filter((section): section is ReportSectionsType => !!section && section.pageLocation === location) ?? [];

  const isSectionErrored = (sections: ReportSectionsType[]): boolean =>
    sections.some((section) => section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.Failure);

  const isSectionGenerated = (sections: ReportSectionsType[]): boolean =>
    sections.some(
      (section) =>
        section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPredicted ||
        section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.Pending ||
        section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.ManualEntry,
    );

  const isSectionGenerating = (sections: ReportSectionsType[]): boolean =>
    sections.some(
      (section) => section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.SectionPredictionPending,
    );

  const isSectionPending = (sections: ReportSectionsType[]): boolean =>
    sections.length === 0 ||
    sections.every((section) => section.aiGeneratedStatus === ApiReportSectionsAiGeneratedStatusChoices.Pending);

  const reportFiles = report?.reportFiles?.edges?.map((edge) => edge?.node) ?? [];
  const page1Sections = getFilteredSections("report-part-1");
  const page2Sections = getFilteredSections("report-part-2");
  const treatmentPlan = report?.reportTreatmentPlan;

  const lifecycleStatus = getReportLifecycleStatus(report);

  type StatusRule = {
    condition: () => boolean;
    result: { status: StatusType; redirectRoute: InitialAssessmentPageRoute };
  };

  // Initial Assessments collapse all in-progress sub-states to "Draft" at the
  // table level. Redirect routes still vary so clicking a Draft row sends the
  // user to the right step.
  const statusRules: StatusRule[] = [
    {
      // Lifecycle past Draft (Complete | Submitted | Needs Revision | Approved)
      // sends the user straight to review-and-sign. The "Needs Revision" row
      // stays on this status even when the backend has flipped reportStatus
      // back to Draft for an active edit, because the helper reads from the
      // PayorReportSubmissionSet first.
      condition: () => lifecycleStatus !== "Draft",
      result: { status: lifecycleStatus, redirectRoute: "review-and-sign" },
    },
    {
      condition: () => isSectionGenerated(page2Sections),
      result: { status: "Draft", redirectRoute: "review-and-sign" },
    },
    {
      condition: () => isSectionErrored(page2Sections),
      result: { status: "Draft", redirectRoute: "report-part-2" },
    },
    {
      condition: () => isSectionGenerating(page2Sections),
      result: { status: "Draft", redirectRoute: "report-part-2" },
    },
    {
      condition: () =>
        treatmentPlan?.aiStgPredictionStatus === ApiReportTreatmentPlanAiStgPredictionStatusChoices.Failure,
      result: { status: "Draft", redirectRoute: "short-term-goals" },
    },
    {
      condition: () =>
        treatmentPlan?.aiTargetPredictionStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.Failure,
      result: { status: "Draft", redirectRoute: "short-term-goals" },
    },
    {
      condition: () =>
        treatmentPlan?.aiTargetPredictionStatus ===
        ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionPending,
      result: { status: "Draft", redirectRoute: "short-term-goals" },
    },
    {
      condition: () =>
        treatmentPlan?.aiStgPredictionStatus ===
        ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionPending,
      result: { status: "Draft", redirectRoute: "short-term-goals" },
    },
    {
      condition: () =>
        (treatmentPlan?.aiStgPredictionStatus ===
          ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionPredicted ||
          treatmentPlan?.aiStgPredictionStatus ===
            ApiReportTreatmentPlanAiStgPredictionStatusChoices.GoalPredictionSkipped) &&
        (treatmentPlan?.aiTargetPredictionStatus ===
          ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionPredicted ||
          treatmentPlan?.aiTargetPredictionStatus ===
            ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionSkipped),
      result: { status: "Draft", redirectRoute: "short-term-goals" },
    },
    {
      condition: () => isSectionErrored(page1Sections),
      result: { status: "Draft", redirectRoute: "report-part-1" },
    },
    {
      condition: () => isSectionGenerating(page1Sections),
      result: { status: "Draft", redirectRoute: "report-part-1" },
    },
    {
      condition: () => isSectionGenerated(page1Sections) || page1Sections.length > 0,
      result: { status: "Draft", redirectRoute: "report-part-1" },
    },
    {
      condition: () => !report?.reportClientDetails,
      result: { status: "Draft", redirectRoute: "client-details" },
    },
    {
      condition: () => reportFiles.length === 0,
      result: { status: "Draft", redirectRoute: "upload-files" },
    },
    {
      condition: () => reportFiles.length > 0 && isSectionPending(page1Sections),
      result: { status: "Draft", redirectRoute: "upload-files" },
    },
  ];

  const defaultResult: { status: StatusType; redirectRoute: InitialAssessmentPageRoute } = {
    status: "Draft",
    redirectRoute: "upload-files",
  };

  return statusRules.find((rule) => rule.condition())?.result ?? defaultResult;
};
